import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, FormControl, MenuItem, Select, TextField, Tooltip } from '@mui/material'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import { useNavigate, useLocation } from 'react-router-dom'
import { isNil } from 'lodash'

import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import TableShared from '../../components/TableShared'
import AddButton from '../../components/AddButton'
import AddQuoteItem from '../../components/quotes/AddQuoteItem'
import Loading from '../../components/Loading'
import quoteStatusAPI from '../../services/api/Quotes/quoteStatusAPI'
import { handleAxiosError, isValid, canViewPrice, formatCurrency, formatQuantity } from '../../helpers/helper'
import { TEXTFIELD_STYLE } from '../../constants/constants'
import { useAuth } from '../../hooks/auth'

const CreateQuote = () => {

    const { user } = useAuth({ middleware: 'guest' })
    const { t } = useTranslation()
    const { company_id, config, choosesite, features, currencySymbol, setCompany_id, setChoosesite } = useStateContext()
    const navigate = useNavigate()
    const location = useLocation()

    /* state */
    const [isLoading, setIsLoading] = useState(false)
    const [customers, setCustomers] = useState([])
    const [shipToLocations, setShipToLocations] = useState([])
    const [salespeople, setSalespeople] = useState([])
    const [quoteItems, setQuoteItems] = useState([])
    const [quoteStatuses, setQuoteStatuses] = useState([])
    const [openQuoteItem, setOpenQuoteItem] = useState(false)
    const [editingQuoteItem, setEditingQuoteItem] = useState(null)
    const [disabled, setDisabled] = useState(true)

    /* form */
    const [customerId, setCustomerId] = useState('')
    const [customerName, setCustomerName] = useState('')
    const [shipToAddressId, setShipToAddressId] = useState('')
    const [address, setAddress] = useState('')
    const [zip, setZip] = useState('')
    const [city, setCity] = useState('')
    const [state, setState] = useState('')
    const [salespersonId, setSalespersonId] = useState('')
    const [salespersonName, setSalespersonName] = useState('')
    const [quoteDate, setQuoteDate] = useState(dayjs())
    const [validUntil, setValidUntil] = useState(dayjs().add(30, 'day'))
    const [requestedDeliveryDate, setRequestedDeliveryDate] = useState(dayjs())
    const [quoteStatus, setQuoteStatus] = useState('draft')
    const [customerReference, setCustomerReference] = useState('')
    const [comments, setComments] = useState('')

    const goBack = () => {
        navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : '/quotes')
    }

    const isValidDate = (value) => isValid(value) && dayjs(value).isValid()

    const isFormValid = () => (
        isValid(customerId)
        && isValid(shipToAddressId)
        && isValid(salespersonId)
        && isValidDate(quoteDate)
        && isValidDate(validUntil)
    )

    /* KEY EVENT */
    useEffect(() => {
        function handleKeyDown(e) {
            if (e.keyCode == '27') {
                goBack()
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [location])

    useEffect(() => {
        if (!isValid(company_id)) {
            setCompany_id(localStorage.getItem('company_id'))
        }
        if (!isValid(choosesite)) {
            setChoosesite(localStorage.getItem('site'))
        }
    }, [])

    useEffect(() => {
        if (isValid(company_id)) {
            getCustomers(company_id)
            getSalespeople(company_id)
        }
    }, [company_id])

    useEffect(() => {
        getQuoteStatuses()
    }, [])

    useEffect(() => {
        setQuoteItems([])
        if (isValid(customerId)) {
            getShipToLocations(customerId)
        } else {
            setShipToLocations([])
            setShipToAddressId('')
            setAddress('')
            setZip('')
            setCity('')
            setState('')
        }
    }, [customerId])

    /* requests */

    const getCustomers = async (id) => {
        await axios
            .get(`/api/customers?company_id=${id}&blocked=0`, config)
            .then(res => {
                setCustomers(res.data)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getQuoteStatuses = async () => {
        await quoteStatusAPI.getQuoteStatuses(config)
            .then((data) => {
                setQuoteStatuses(data)
            })
    }

    const getSalespeople = async (id) => {
        await axios
            .get(`/api/users?company_id=${id}&role=salesperson`, config)
            .then(res => {
                setSalespeople(res.data?.data ?? res.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getShipToLocations = async (id) => {
        setShipToAddressId('')
        setAddress('')
        setZip('')
        setCity('')
        setState('')

        await axios
            .get(`/api/ship-addresses?customer_id=${id}`, config)
            .then(res => {
                const data = res.data
                setShipToLocations(data)
                setShipToAddressId(data[0]?.id || '')
                setAddress(data[0]?.address || '')
                setZip(data[0]?.zip || '')
                setCity(data[0]?.city || '')
                setState(data[0]?.state || '')
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const createQuote = async (e) => {
        e.preventDefault()
        setIsLoading(true)

        const formData = {}

        formData['customer_id'] = customerId
        formData['ship_address_id'] = shipToAddressId
        formData['salesperson_id'] = salespersonId
        formData['quote_date'] = dayjs(quoteDate).format('YYYY-MM-DD')
        formData['valid_until'] = dayjs(validUntil).format('YYYY-MM-DD')
        formData['requested_delivery_date'] = dayjs(requestedDeliveryDate).format('YYYY-MM-DD')
        formData['quote_status'] = quoteStatus
        formData['customer_reference'] = customerReference
        formData['comments'] = comments
        formData['site_id'] = Number(choosesite)
        formData['quote_items'] = quoteItems

        await axios.post(`/api/quotes`, formData, config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data?.success?.message
            })

            const createdId = data?.success?.created?.id
            if (createdId) {
                navigate(`/quotes/${createdId}`, { state: { prevPathname: location.state?.prevPathname ?? '/quotes' } })
            } else {
                goBack()
            }
        }).catch(({ response }) => {
            handleAxiosError({ response: response, showDescription: true })
        }).finally(() => {
            setIsLoading(false)
        })
    }

    /* handlers */

    const handleCustomerChange = (a, b) => {
        setCustomerId(b?.id || '')
        setCustomerName(b?.label || '')
    }

    const handleSalespersonChange = (a, b) => {
        setSalespersonId(b?.id || '')
        setSalespersonName(b?.label || '')
    }

    const handleShipToLocation = (e) => {
        const id = e.target.value
        setShipToAddressId(id)
        const shipToLocation = shipToLocations.find(target => target.id === id)
        setAddress(shipToLocation?.address ?? '')
        setZip(shipToLocation?.zip ?? '')
        setCity(shipToLocation?.city ?? '')
        setState(shipToLocation?.state ?? '')
    }

    const handleOpenQuoteItem = () => {
        setDisabled(true)
        setEditingQuoteItem(null)
        setOpenQuoteItem(!openQuoteItem)
    }

    const handleEditQuoteItem = (item) => {
        setEditingQuoteItem(item)
        setDisabled(false)
        setOpenQuoteItem(true)
    }

    const getColumns = () => {
        return [
            {
                field: 'item_no',
                headerName: t('item__no'),
                flex: 0.8,
                minWidth: 90
            },
            {
                field: 'description',
                headerName: t('description'),
                flex: 1.5,
                minWidth: 140
            },
            {
                field: 'unit_of_measure_code',
                headerName: t('uom'),
                flex: 0.7,
                minWidth: 80,
                align: 'center',
                headerAlign: 'center'
            },
            {
                field: 'quantity',
                headerName: t('quantity'),
                flex: 1,
                minWidth: 110,
                align: 'center',
                headerAlign: 'center',
                valueFormatter: ({ value }) => isValid(value) ? formatQuantity(value) : ''
            },
            {
                field: 'source_vendor_name',
                headerName: t('vendor'),
                flex: 1.2,
                minWidth: 120
            },
            ...(canViewPrice(user, features) ? [
                {
                    field: 'source_cost',
                    headerName: t('source_cost'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'freight_cost',
                    headerName: t('freight_cost'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'fuel_surcharge',
                    headerName: t('fuel_surcharge'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'other_costs',
                    headerName: t('other_costs'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'calculated_cost',
                    headerName: t('calculated_cost'),
                    flex: 1,
                    minWidth: 120,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'unit_price',
                    headerName: t('unit_price'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'total_price',
                    headerName: t('total_price'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'margin_dollar',
                    headerName: t('margin_dollar'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${currencySymbol}${formatCurrency(value)}` : t('coming_soon')
                },
                {
                    field: 'margin_percent',
                    headerName: t('margin_percent'),
                    flex: 1,
                    minWidth: 110,
                    align: 'center',
                    headerAlign: 'center',
                    valueFormatter: ({ value }) => isValid(value) ? `${formatCurrency(value)}%` : t('coming_soon')
                }
            ] : []),
            {
                field: 'actions',
                headerName: t('actions'),
                sortable: false,
                flex: 0.7,
                minWidth: 75,
                cellClassName: 'padding-0',
                renderCell: (params) => <ItemActions params={params} t={t} quoteItems={quoteItems} setQuoteItems={setQuoteItems} onEdit={handleEditQuoteItem} />
            }
        ]
    }

    const optionsCustomers = customers?.map(option => ({ label: option.customer_no + ' - ' + option.name, id: option.id }))
    const optionsSalespeople = salespeople?.map(option => ({ label: option.name, id: option.id }))

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <Box sx={{ background: '#F5F5FD', minHeight: '100vh' }}>
                <div className='flex justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                    <div className='flex items-center justify-between w-full leading-none'>
                        <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#336195] text-[35px] mr-4'>•</span> {t('create_new_quote')}</p>
                        <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                            <button onClick={goBack}><i className="fa-solid fa-plus"></i></button>
                        </div>
                    </div>
                </div>
                <div className='p-5 pb-12 flex justify-between gap-3 flex-col lg:flex-row'>
                    <div className='bg-white p-6 pt-0 rounded-md w-full lg:w-2/5 h-fit edit-view'>
                        <div className='pb-11 pt-1'>
                            <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('general_information')}</p>
                        </div>

                        {/* Quote No. */}
                        {/* <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('quote_no')}</p>
                            <div className='w-full'>
                                <TextField
                                    sx={TEXTFIELD_STYLE}
                                    type="text"
                                    name="quote_number"
                                    value=""
                                    placeholder={t('auto_generated')}
                                    disabled
                                />
                            </div>
                        </div> */}

                        {/* Customer */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('customer')} *</p>
                            <div className='w-full'>
                                <Autocomplete
                                    disablePortal
                                    disableClearable
                                    id="customers-autocomplete"
                                    options={optionsCustomers}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    sx={{
                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                    onChange={handleCustomerChange}
                                    renderInput={(params) => <TextField {...params} autoFocus={true} />}
                                />
                            </div>
                        </div>

                        {/* Ship to location */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('ship_to_location')} *</p>
                            <div className='w-full'>
                                <FormControl sx={{ width: '100%' }}>
                                    <Select
                                        disabled={!isValid(customerId)}
                                        value={shipToAddressId}
                                        onChange={handleShipToLocation}
                                        sx={TEXTFIELD_STYLE}
                                    >
                                        {shipToLocations?.map((item, index) => <MenuItem key={index} value={item.id}>{item.code} - {item.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        {shipToAddressId ? (
                            <div className='w-full p-4 address mb-4'>
                                <div className='w-full'>{address}</div>
                                <div className='w-full'>{zip} {city}, {state}</div>
                            </div>
                        ) : null}

                        {/* Quote Date */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('quote_date')} *</p>
                            <div className='w-full'>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <MobileDatePicker
                                        sx={TEXTFIELD_STYLE}
                                        format="YYYY-MM-DD"
                                        value={quoteDate}
                                        onChange={(newValue) => {
                                            setQuoteDate(newValue)
                                            if (newValue && validUntil && dayjs(validUntil).isBefore(dayjs(newValue), 'day')) {
                                                setValidUntil(null)
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                            </div>
                        </div>

                        {/* Valid Until */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('valid_until')} *</p>
                            <div className='w-full'>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <MobileDatePicker
                                        sx={TEXTFIELD_STYLE}
                                        format="YYYY-MM-DD"
                                        value={validUntil}
                                        minDate={quoteDate || undefined}
                                        onChange={(newValue) => setValidUntil(newValue)}
                                    />
                                </LocalizationProvider>
                            </div>
                        </div>

                        {/* Requested Delivery Date */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('requested_delivery_date')}</p>
                            <div className='w-full'>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <MobileDatePicker
                                        sx={TEXTFIELD_STYLE}
                                        format="YYYY-MM-DD"
                                        value={requestedDeliveryDate}
                                        onChange={(newValue) => setRequestedDeliveryDate(newValue)}
                                    />
                                </LocalizationProvider>
                            </div>
                        </div>

                        {/* Salesperson */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('salesperson_owner')} *</p>
                            <div className='w-full'>
                                <Autocomplete
                                    disablePortal
                                    id="salesperson-autocomplete"
                                    options={optionsSalespeople}
                                    value={salespersonId ? { label: salespersonName, id: salespersonId } : null}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    sx={{
                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                    onChange={handleSalespersonChange}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>
                        </div>

                        {/* Quote Status */}
                        {/* <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('quote_status')}</p>
                            <div className='w-full'>
                                <FormControl sx={{ width: '100%' }}>
                                    <Select
                                        disabled
                                        value={quoteStatus}
                                        onChange={(e) => setQuoteStatus(e.target.value)}
                                        sx={TEXTFIELD_STYLE}
                                    >
                                        {quoteStatuses.map((status) => (
                                            <MenuItem key={status.value} value={status.value}>{status.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div> */}

                        {/* Customer Reference */}
                        <div className='w-full pb-4'>
                            <p className="text-[14px] text-[#718096]">{t('customer_reference')}</p>
                            <div className='w-full'>
                                <TextField
                                    sx={TEXTFIELD_STYLE}
                                    type="text"
                                    name="customer_reference"
                                    value={customerReference}
                                    onChange={(e) => setCustomerReference(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='w-full lg:w-3/5 h-fit'>
                        <div className='bg-white pt-0 rounded-md w-full edit-view mb-3'>
                            <div className='pt-1 pl-3'>
                                <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('quote_items')}</p>
                            </div>
                            <div className='flex justify-end p-5 pt-0 border-b'>
                                <Tooltip disableInteractive title={t('add_quote_item')} placement='bottom'>
                                    <div>
                                        <AddButton disabled={!isValid(customerId)} onClick={handleOpenQuoteItem}><i className="fa-solid fa-plus"></i></AddButton>
                                    </div>
                                </Tooltip>
                            </div>
                            <div className='border-b'>
                                {quoteItems.length > 0 ? (
                                    <div className='flex justify-end w-full p-2'>
                                        <button className='px-2 py-1 text-white bg-red-600 font-bold rounded-md mb-1 roboto text-xs uppercase' onClick={() => setQuoteItems([])}>{t('clear_all')}</button>
                                    </div>
                                ) : null}
                                <TableShared columns={getColumns()} items={quoteItems} disableColumnMenu={true} />
                            </div>
                        </div>

                        <div className='w-full'>
                            <div className='bg-white pt-0 rounded-md w-full edit-view mb-3'>
                                <div className='pt-1 p-5'>
                                    <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('comments')}</p>
                                </div>
                                <div className='w-full p-5 pt-0'>
                                    <p className="text-[14px] text-[#718096]">{t('comments_text')}</p>
                                    <TextField
                                        multiline
                                        rows={3}
                                        value={comments}
                                        onChange={e => setComments(e.target.value)}
                                        sx={TEXTFIELD_STYLE}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className='w-full flex justify-end'>
                            <button type="button" onClick={createQuote} className='text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#DBA34B] hover:bg-[#CF7300] disabled:bg-[#B8B7BC]'>
                                <span className='pr-2'><i className="fa-solid fa-plus"></i></span> {t('create')}
                            </button>
                        </div>
                    </div>
                </div>
            </Box>

            <AddQuoteItem open={openQuoteItem} handleOpen={handleOpenQuoteItem} quoteItems={quoteItems} setQuoteItems={setQuoteItems} disabled={disabled} setDisabled={setDisabled} editingItem={editingQuoteItem} customerId={customerId} />
        </>
    )
}

export default CreateQuote

const ItemActions = ({ params, t, quoteItems, setQuoteItems, onEdit }) => {
    const deleteItem = (id) => {
        setQuoteItems(quoteItems.filter(i => i.id != id))
    }

    return (
        <div className='flex justify-between'>
            <Tooltip disableInteractive title={t('update')} placement='bottom'>
                <div style={{ color: 'rgba(0,0,0,.54)' }}>
                    <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => onEdit(params.row)}><i className="fa-solid fa-pencil"></i></span>
                </div>
            </Tooltip>
            <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                <div style={{ color: 'rgba(0,0,0,.54)' }}>
                    <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => deleteItem(params.row.id)}><i className="fa-solid fa-trash"></i></span>
                </div>
            </Tooltip>
        </div>
    )
}
